import { useRef } from 'react'

import { Box, Link, Text } from '@factorialco/deprecated-design-system'
import {
  AtsConversationMessagesDocument,
  AtsJobApplicationMessagesPageFragment,
  AtsMessageBoardConversationDocument,
  AtsMessageBoardConversationFragment,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { MessagesList } from './MessageList'
import { MessageListSkeleton } from './MessageList/MessageListSkeleton'
import { NewMessage } from './NewMessage/NewMessage'

interface Props {
  conversation?: AtsMessageBoardConversationFragment | undefined
  application?: AtsJobApplicationMessagesPageFragment | undefined
  companyId: string
}

export const MessageBoard = ({ conversation, application, companyId }: Props) => {
  const scrollRef = useRef<HTMLElement>(null)

  const { data, loading } = useQuery(AtsMessageBoardConversationDocument, {
    variables: {
      companyId,
      atsApplicationIds: [String(application?.id)],
    },
    skip: !application?.id,
  })
  const applicationConversation = data?.ats?.conversationsConnection?.nodes[0]
  const boardConversation = conversation || applicationConversation
  const conversationApplication = conversation?.application || application
  const hasMessageTemplatesFeature =
    data?.features?.featuresConnection?.nodes?.find(
      (featureFlag) => featureFlag.name === 'ats_message_templates'
    )?.enabled ?? false

  const { data: messagesData, loading: loadingMessages } = useQuery(
    AtsConversationMessagesDocument,
    {
      variables: { atsConversationId: String(boardConversation?.id) },
      skip: !boardConversation?.id,
    }
  )

  if (!conversationApplication) return null

  const candidateFullName = conversationApplication.candidate.fullName
  const messages = messagesData?.ats.messagesConnection.nodes || []

  if (loading || loadingMessages)
    return <MessageListSkeleton messagesPerGroup={application?.id ? 2 : 4} />

  return (
    <Box height="full" justifyContent="spaceBetween" gap="s24">
      {boardConversation && messages.length > 0 ? (
        <MessagesList conversation={boardConversation} messages={messages} scrollRef={scrollRef} />
      ) : (
        <Box padding="s40" gap="s16" width="full" alignItems="center" flexGrow>
          <Text.Normal>{i18n.t('ats.candidates.messages.list.no_messages_subtitle')}</Text.Normal>
          <Link.Href href="https://help.factorialhr.com/ats-message-board">
            {i18n.t('ats.candidates.messages.list.no_messages_help_link')}
          </Link.Href>
        </Box>
      )}

      {candidateFullName && conversationApplication.candidate.email && (
        <NewMessage
          applicationId={conversationApplication.id}
          candidateFullName={candidateFullName}
          scrollRef={scrollRef}
          hasMessageTemplatesFeature={hasMessageTemplatesFeature}
        />
      )}
    </Box>
  )
}
